import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Loader2 } from 'lucide-react'
import type { EmailTemplate } from '../types'

interface TemplateEditorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: EmailTemplate | null
  onSave: (data: TemplateFormData) => Promise<void>
  isLoading?: boolean
}

export interface TemplateFormData {
  name: string
  slug: string
  subject: string
  description: string
  htmlContent: string
  textContent: string
  category: string
  variables: string[]
}

export function TemplateEditorDialog({
  open,
  onOpenChange,
  template,
  onSave,
  isLoading,
}: TemplateEditorDialogProps) {
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    slug: '',
    subject: '',
    description: '',
    htmlContent: '',
    textContent: '',
    category: 'custom',
    variables: [],
  })
  const [variablesInput, setVariablesInput] = useState('')
  const [activeTab, setActiveTab] = useState('html')

  useEffect(() => {
    if (template) {
      const vars = typeof template.variables === 'string'
        ? JSON.parse(template.variables)
        : template.variables
      setFormData({
        name: template.name,
        slug: template.slug,
        subject: template.subject,
        description: template.description || '',
        htmlContent: template.htmlContent || '',
        textContent: template.textContent || '',
        category: template.category,
        variables: vars || [],
      })
      setVariablesInput(Array.isArray(vars) ? vars.join(', ') : '')
    } else {
      setFormData({
        name: '',
        slug: '',
        subject: '',
        description: '',
        htmlContent: '',
        textContent: '',
        category: 'custom',
        variables: [],
      })
      setVariablesInput('')
    }
  }, [template, open])

  const handleSave = async () => {
    const variables = variablesInput
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v)

    await onSave({ ...formData, variables })
    onOpenChange(false)
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  const isEdit = !!template
  const title = isEdit ? 'Edit Template' : 'Create Template'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the email template configuration and content.'
              : 'Create a new email template with HTML and plain text versions.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value })
                  if (!isEdit) {
                    setFormData((prev) => ({ ...prev, slug: generateSlug(e.target.value) }))
                  }
                }}
                placeholder="e.g., Welcome Email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="e.g., welcome-email"
                disabled={isEdit}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transactional">Transactional</SelectItem>
                  <SelectItem value="notification">Notification</SelectItem>
                  <SelectItem value="engagement">Engagement</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="variables">Variables (comma-separated)</Label>
              <Input
                id="variables"
                value={variablesInput}
                onChange={(e) => setVariablesInput(e.target.value)}
                placeholder="e.g., firstName, artworkName, shopUrl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="e.g., Welcome to Arthaus, {{firstName}}!"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of when this template is used"
            />
          </div>

          <div className="space-y-2">
            <Label>Email Content</Label>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="html">HTML</TabsTrigger>
                <TabsTrigger value="text">Plain Text</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>

              <TabsContent value="html" className="mt-2">
                <Textarea
                  value={formData.htmlContent}
                  onChange={(e) => setFormData({ ...formData, htmlContent: e.target.value })}
                  placeholder="Enter HTML content..."
                  rows={12}
                  className="font-mono text-sm"
                />
              </TabsContent>

              <TabsContent value="text" className="mt-2">
                <Textarea
                  value={formData.textContent}
                  onChange={(e) => setFormData({ ...formData, textContent: e.target.value })}
                  placeholder="Enter plain text content..."
                  rows={12}
                  className="font-mono text-sm"
                />
              </TabsContent>

              <TabsContent value="preview" className="mt-2">
                <div className="border rounded-lg p-4 bg-white min-h-[300px]">
                  {formData.htmlContent ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: formData.htmlContent }}
                    />
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      Add HTML content to see preview
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!formData.name || !formData.slug || !formData.subject || isLoading}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
